<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">
              {{ $t("ELIXIOPAY.TRANSACTIONS_LIST") }}
            </h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openTransactionCreateModal"
              v-if="$currentUserCan($permissions.PERM_CREATE_NUVEI_CARDS)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">{{
                $t("NUVEI_CARDS.ADD_NUVEI_CARD")
              }}</span>
            </base-button>
            <notification-subscription
              v-if="
                $currentUserCan($permissions.PERM_VIEW_ELIXIOPAY_TRANSACTIONS)
              "
              :objectType="'elixiopayTransactions'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <transaction-list-table
        @onViewTransaction="openTransactionViewModal"
        @onResyncTransaction="resyncTransaction"
        @onDeleteTransaction="deleteTransaction"
        :key="renderKey * 100"
      />

      <div
        v-if="isViewTransactionModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isViewTransactionModalOpened ? 'show' : 'hide']"
        :key="renderKey * 200"
      >
        <div
          class="resizable-wrapper-content"
          v-resizable-modal="'ELIXIOPAY_TRANSACTION_VIEW'"
        >
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("ELIXIOPAY.VIEW_TRANSACTION") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <notification-subscription
                v-if="openTransaction"
                :objectType="'elixiopayTransactions'"
                :objectId="openTransaction.id"
                :events="{
                  UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                  DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
                }"
              />
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button
                  class="sync"
                  @click="resyncTransaction(openTransaction)"
                >
                  <i class="fal fa-sync-alt"></i>
                  <span>{{ $t("COMMON.RESYNC") }}</span>
                </button>
                <button
                  class="delete"
                  @click="deleteTransaction(openTransaction)"
                >
                  <i class="fal fa-trash-alt"></i>
                  <span>{{ $t("COMMON.DELETE") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeTransactionModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <view-transaction-component
              v-if="openTransaction"
              :transactionId="openTransaction.id"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isEditTransactionModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isEditTransactionModalOpened ? 'show' : 'hide']"
        :key="renderKey * 300"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'NUVEI_CARD'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("NUVEI_CARDS.EDIT_NUVEI_CARD") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <base-dropdown
                title-classes="btn dropdown-button"
                menu-on-right
                :has-toggle="false"
              >
                <template slot="title">
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <button
                  class="view"
                  @click="openTransactionViewModal(openTransaction)"
                >
                  <i class="fal fa-expand-alt"></i>
                  <span>{{ $t("COMMON.VIEW") }}</span>
                </button>
              </base-dropdown>
              <button class="close" @click="closeTransactionModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <edit-transaction-component
              v-if="openTransaction"
              :transactionId="openTransaction.id"
              @onViewTransaction="openTransactionViewModal"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isAddTransactionModalOpened"
        class="resizable-wrapper"
        v-bind:class="[isAddTransactionModalOpened ? 'show' : 'hide']"
        :key="renderKey * 400"
      >
        <div class="resizable-wrapper-content" v-resizable-modal="'NUVEI_CARD'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                {{ $t("NUVEI_CARDS.ADD_NUVEI_CARD") }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeTransactionModal">
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <add-transaction-component
              @onViewTransaction="openTransactionViewModal"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  QUERY_ACTIONS_VIEW,
  QUERY_ACTIONS_EDIT,
  QUERY_ACTIONS_ADD,
} from "@/constants/common";
import swal from "sweetalert2";
import { Button } from "element-ui";
import requestErrorMixin from "@/mixins/request-error-mixin";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import TransactionListTable from "./partials/TransactionListTable.vue";
import ViewTransactionComponent from "./components/ViewTransactionComponent.vue";
import EditTransactionComponent from "./components/EditTransactionComponent.vue";
import AddTransactionComponent from "./components/AddTransactionComponent.vue";

export default {
  layout: "DashboardLayout",

  components: {
    TransactionListTable,
    NotificationSubscription,
    ViewTransactionComponent,
    EditTransactionComponent,
    AddTransactionComponent,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  data() {
    const transactionId = this.$route.query.id;
    const action = this.$route.query.action;
    let isViewTransactionModalOpened = false;
    let isEditTransactionModalOpened = false;
    let isAddTransactionModalOpened = false;
    let openTransaction = null;
    if (transactionId && action) {
      if (action === QUERY_ACTIONS_VIEW) {
        isViewTransactionModalOpened = true;
      } else if (action === QUERY_ACTIONS_EDIT) {
        isEditTransactionModalOpened = true;
      }
      openTransaction = { id: transactionId };
    } else if (action === QUERY_ACTIONS_ADD) {
      isAddTransactionModalOpened = true;
    }
    return {
      isViewTransactionModalOpened: isViewTransactionModalOpened,
      isEditTransactionModalOpened: isEditTransactionModalOpened,
      isAddTransactionModalOpened: isAddTransactionModalOpened,
      openTransaction: openTransaction,
      renderKey: 1,
    };
  },

  methods: {
    openTransactionCreateModal() {
      this.closeTransactionModal();
      this.isAddTransactionModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List ElixioPayTransactions",
          query: { action: QUERY_ACTIONS_ADD },
        }).href
      );
    },
    openTransactionViewModal(transaction, reRender = false) {
      this.closeTransactionModal();
      this.openTransaction = transaction;
      this.isViewTransactionModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List ElixioPayTransactions",
          query: { id: this.openTransaction.id, action: QUERY_ACTIONS_VIEW },
        }).href
      );
    },
    openTransactionEditModal(transaction) {
      this.closeTransactionModal();
      this.openTransaction = transaction;
      this.isEditTransactionModalOpened = true;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List ElixioPayTransactions",
          query: { id: this.openTransaction.id, action: QUERY_ACTIONS_EDIT },
        }).href
      );
    },
    /* openTransactionViewModal(transaction, reRender = false) {
      this.closeTransactionModal();
      this.openTransaction = transaction;
      this.isViewTransactionModalOpened = true;
      if (reRender) {
        this.renderKey++;
      }

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List PandaPayTransactions",
          query: { id: this.openTransaction.id, action: QUERY_ACTIONS_VIEW },
        }).href
      );
    }, */

    closeTransactionModal() {
      this.isAddTransactionModalOpened = false;
      this.isViewTransactionModalOpened = false;
      this.isEditTransactionModalOpened = false;
      this.openTransaction = null;

      history.pushState(
        {},
        null,
        this.$router.resolve({
          name: "List ElixioPayTransactions",
          query: {},
        }).href
      );
    },

    async resyncTransaction(transaction) {
      const confirmation = await swal.fire({
        title: this.$t("ELIXIOPAY.RESYNC_THIS_TRANSACTION"),
        text: this.$t("ELIXIOPAY.RESYNC_CONFIRMATION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch(
            "elixiopayTransactions/resync",
            transaction.id
          );
          this.renderKey++;
          this.$notify({
            type: "success",
            message: this.$t("ELIXIOPAY.TRANSACTION_RESYNCED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async deleteTransaction(transaction) {
      const confirmation = await swal.fire({
        title: this.$t("ELIXIOPAY.DELETE_THIS_TRANSACTION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch(
            "elixiopayTransactions/destroy",
            transaction.id
          );
          this.renderKey++;
          this.closeTransactionModal();
          this.$notify({
            type: "success",
            message: this.$t("ELIXIOPAY.TRANSACTION_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
